import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { OptionModel } from '../../../../models/option.model';
import { FancyButtonComponent } from '../../../../common-components/fancy-button/fancy-button.component';
import { Deviation } from '../../../../interfaces/deviation.interface';
import { ButtonType } from '../../../../common-components/fancy-button/button-type.enum';
import { TranslocoDirective } from '@jsverse/transloco';


@Component({
  selector: 'app-option-view',
  standalone: true,
  imports: [
    NgForOf,
    NgClass,
    NgIf,
    FancyButtonComponent,
    TranslocoDirective
  ],
  templateUrl: './option-view.component.html',
  styleUrl: './option-view.component.scss'
})
export class OptionViewComponent implements OnInit {
  @Input() public options: OptionModel[] | Deviation[] = [];
  @Output() public optionsChange: EventEmitter<OptionModel[]> = new EventEmitter<OptionModel[]>();
  @Input() public selected: Deviation | null = null;
  @Output() public selectedChange: EventEmitter<Deviation> = new EventEmitter<Deviation>();
  @Input() public showErrorMessage: boolean = false;
  protected isValid: boolean = false;


  ngOnInit() {
    if (this.selected) {
      this.isValid = true;
      return;
    }

    this.isValid = this.options.some(o => this.isOptionModel(o) && o.selected);
  }

  onClick(index: number): void {
    if (!this.options.length) {
      return;
    }

    if (!this.isOptionModel(this.options[0])) {
      const optionsList = this.options as Deviation[];
      this.selectedChange.emit(optionsList[index]);
      this.isValid = true;
      return;
    }

    const optionsList = this.options as OptionModel[];

    optionsList.forEach(o => o.selected = false);
    if (optionsList[index]) {
      optionsList[index].selected = !optionsList[index].selected;
    }

    this.isValid = optionsList.some(o => o.selected);
    this.optionsChange.emit(optionsList);
  }

  selectedOptionHasHint(): string | undefined {
    return this.selected?.hint;
  }

  protected getTitle(o: OptionModel | Deviation): string {
    return this.isOptionModel(o) ? o.name : o.label;
  }

  protected getSelected(o: OptionModel | Deviation): boolean {
    return this.isOptionModel(o) ? o.selected : o.id === this.selected?.id;
  }

  protected getIcon(o: OptionModel | Deviation): string {
    return this.isOptionModel(o) ? o.icon : '';
  }

  protected getType(o: OptionModel | Deviation): ButtonType | undefined {
    return this.isOptionModel(o) ? o.type : undefined;
  }

  protected getText(o: OptionModel | Deviation): string {
    return this.isOptionModel(o) ? '' : o.subLabel;
  }

  public setSelected(o: OptionModel | Deviation, event: boolean): void {
    if (!this.isOptionModel(o)) {
      this.selected = o;
      this.selectedChange.emit(o);
      return;
    }

    const optionsList = this.options as OptionModel[];

    const targetOption = optionsList.find(x => x?.name === o.name);

    if (targetOption) {
      targetOption.selected = event;
      this.optionsChange.emit(optionsList);
    }
  }

  private isOptionModel(o: OptionModel | Deviation): o is OptionModel {
    return !!o && typeof o === 'object' && 'selected' in o && 'name' in o;
  }
}
